// Ecwid JS API bridge. Ecwid remains the source of truth for cart & checkout.
// The REST secret never leaves the backend; the browser only uses the public
// storefront script + JS cart API. NO location.hash manipulation anywhere.

const STORE_ID = process.env.REACT_APP_ECWID_STORE_ID;

export const ecwidConfigured = Boolean(STORE_ID);

let loadingPromise = null;

export function loadEcwid() {
  if (!STORE_ID) return Promise.resolve(false);
  if (window.Ecwid && window.Ecwid.Cart) return Promise.resolve(true);
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve) => {
    window.ecwid_script_defer = true;
    window.ecwid_dynamic_widgets = true;
    window._xnext_initialization_scripts = window._xnext_initialization_scripts || [];

    const script = document.createElement("script");
    script.id = "ecwid-script";
    script.charset = "utf-8";
    script.async = true;
    script.src = `https://app.ecwid.com/script.js?${STORE_ID}&data_platform=code`;
    document.body.appendChild(script);

    const started = Date.now();
    const timer = setInterval(() => {
      if (window.Ecwid && window.Ecwid.Cart && typeof window.Ecwid.Cart.get === "function") {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started > 20000) {
        clearInterval(timer);
        resolve(false);
      }
    }, 120);
  });
  return loadingPromise;
}

function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out`)), ms)),
  ]);
}

export function getCart() {
  return withTimeout(
    new Promise((resolve, reject) => {
      if (!window.Ecwid?.Cart) return reject(new Error("Ecwid not loaded"));
      window.Ecwid.Cart.get((cart) => resolve(cart));
    }),
    8000,
    "Cart get"
  );
}

export function clearCart() {
  return withTimeout(
    new Promise((resolve, reject) => {
      window.Ecwid.Cart.clear((ok, error) =>
        ok ? resolve() : reject(new Error(error || "Cart clear failed"))
      );
    }),
    8000,
    "Cart clear"
  );
}

export function addProduct(id, quantity, options = {}) {
  return withTimeout(
    new Promise((resolve, reject) => {
      window.Ecwid.Cart.addProduct(
        { id: Number(id), quantity, options },
        (ok, product, cart, error) =>
          ok ? resolve({ product, cart }) : reject(new Error(error || "Could not add product"))
      );
    }),
    10000,
    "Add product"
  );
}

// Mirror our custom cart into the Ecwid browser cart. Ecwid sometimes adds
// items asynchronously without firing the addProduct callback, so the source
// of truth is a final Cart.get comparison — never the callbacks.
export async function syncCartToEcwid(items) {
  const wanted = items.map((i) => ({
    id: Number(i.id),
    quantity: i.qty,
    options: i.options || {},
  }));
  let existing = [];
  try {
    const cart = await getCart();
    existing = (cart.items || []).map((i) => ({
      id: i.product?.id ?? i.productId,
      quantity: i.quantity,
      options: optionsFromEcwidItem(i),
    }));
  } catch (_) {
    /* fall through to rebuild */
  }
  const differs = JSON.stringify(normalize(existing)) !== JSON.stringify(normalize(wanted));
  if (differs) {
    try {
      await clearCart();
    } catch (_) {}
    for (const item of wanted) {
      try {
        await addProduct(item.id, item.quantity, item.options);
      } catch (_) {
        /* verified against final cart below */
      }
    }
  }
  let finalItems = [];
  try {
    const cart = await getCart();
    finalItems = cart.items || [];
  } catch (_) {}
  const failures = wanted.filter((w) => {
    const found = finalItems.find(
      (i) => (i.product?.id ?? i.productId) === w.id && i.quantity === w.quantity
    );
    return !found;
  });
  return { synced: failures.length === 0, failures };
}

function optionsFromEcwidItem(item) {
  const out = {};
  (item.options || []).forEach((o) => {
    if (o.type === "UPLOADFILES") return;
    const vals = (o.selections || []).map((s) => s.title || s.selectionTitle).filter(Boolean);
    if (vals.length === 1) out[o.name] = vals[0];
    else if (vals.length > 1) out[o.name] = vals;
  });
  return out;
}

function normalize(items) {
  return items
    .map((i) => ({ id: i.id, quantity: i.quantity, options: i.options }))
    .sort((a, b) => a.id - b.id);
}

// Initialize the ProductBrowser widget straight to the cart/checkout page.
// (The defaultPage init arg is unreliable with deferred+dynamic loading, so we
// also navigate via the public API on the widget's first rendered page.)
export function initCheckoutWidget(elementId) {
  window._xnext_initialization_scripts = [
    {
      widgetType: "ProductBrowser",
      id: elementId,
      arg: [`id=${elementId}`, "defaultPage=cart"],
    },
  ];
  let navigated = false;
  if (window.Ecwid?.OnPageLoaded?.add) {
    window.Ecwid.OnPageLoaded.add((page) => {
      if (navigated) return;
      navigated = true;
      if (page?.type !== "CART") window.Ecwid.openPage("cart");
    });
  }
  window.Ecwid.init();
}

// Scope branding removal to our mount node; reapplies after widget re-renders.
export function hideEcwidBranding(root) {
  if (!root) return () => {};
  const hide = () =>
    root
      .querySelectorAll('.ecwid-powered-by, [class*="powered-by"]')
      .forEach((el) => el.style.setProperty("display", "none", "important"));
  hide();
  const observer = new MutationObserver(hide);
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
}
